import getConfigValue from '@/config';
import { downloadFile } from '@/services/helpers';
import { useCreateQueuesExportRequestMutation, useLazyCheckQueuesExportLimitQuery } from '@/services/queues';
import { Button, toast, useBreakpoint, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { QueueType, SearchQueuesSort } from 'types';

import { ExportAcknowledgementModal } from '../AccountsExportAcknowledgementModal';

const api = getConfigValue('VITE_APP_API_BASE_URL');

export function buildQueueExportQueryString(
  searchParams: URLSearchParams,
  queueType: QueueType | undefined,
  sort: SearchQueuesSort | undefined,
): string {
  const exportParams = new URLSearchParams();

  searchParams.forEach((value, key) => {
    if (key === 'search') {
      exportParams.set('searchTerm', value);
    } else if (key !== 'page' && key !== 'sort') {
      exportParams.set(key, value);
    }
  });

  if (queueType) {
    exportParams.set('type', queueType);
  }

  // The list view falls back to a default sort when the URL has no `sort` param. Pass the
  // resolved sort through explicitly so the CSV row order matches what the user sees.
  if (sort) {
    exportParams.set('sort', sort);
  }

  const queryString = exportParams.toString();
  return queryString ? `?${queryString}` : '';
}

interface QueuesExportActionProps {
  queueType: QueueType | undefined;
  sort: SearchQueuesSort | undefined;
}

export function QueuesExportAction({ queueType, sort }: QueuesExportActionProps) {
  const { isPhone } = useBreakpoint();
  const [searchParams] = useSearchParams();
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportCheck, { isFetching: isCheckFetching }] = useLazyCheckQueuesExportLimitQuery();
  const [createExportRequest, { isLoading: isCreateExportRequestLoading }] = useCreateQueuesExportRequestMutation();
  const isLoading = isCheckFetching || isCreateExportRequestLoading || isDownloading;
  const { isOpen, handleOpen: openModal, handleClose } = useOpenClose();

  const handleExportClick = useCallback(async () => {
    try {
      const exportQueryString = buildQueueExportQueryString(searchParams, queueType, sort);
      const checkResult = await exportCheck(exportQueryString, false).unwrap();

      if (checkResult.totalWithinLimit) {
        setIsDownloading(true);
        await downloadFile(`${api}/queues/export/csv${exportQueryString}`, `queues_export_v1_${Date.now()}.csv`);
      } else {
        await createExportRequest(exportQueryString).unwrap();
        openModal();
      }
    } catch (error) {
      console.error('Error requesting an export.', error);
      toast.error('Error requesting an export. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  }, [createExportRequest, exportCheck, openModal, queueType, searchParams, sort]);

  return (
    <>
      <Button
        variant="secondary"
        tone="neutral"
        iconPrefix="download"
        size={{ base: 'sm', desktop: 'md' }}
        aria-label="Export"
        onClick={handleExportClick}
        isLoading={isLoading}
        isDisabled={isLoading}
      >
        {!isPhone && 'Export'}
      </Button>
      <ExportAcknowledgementModal handleClose={handleClose} isOpen={isOpen} />
    </>
  );
}
